import React, { Fragment, useRef } from 'react'

import {
  Card,
  Table,
  TableRow,
  TableBody,
  TableContainer,
  SxProps,
  Theme,
  alpha,
} from '@mui/material'
import { TFunction } from 'i18next'
import { startCase } from 'lodash'
import { useTranslation } from 'react-i18next'

import { EmptyData } from '../../pages/Reports'
import { SortDirection } from '../../types'
import Scrollbar from '../Scrollbar/Scrollbar'
import { TableCell, TableHead } from '../TableV2'
import { TableHeadItem } from '../TableV2/types'

import { XlsxValidationByRow, XlsxValidationError } from './types'

// ----------------------------------------------------------------------

const getTableHead = (t: TFunction) =>
  [
    {
      id: 'field',
      label: t('xlsxValidation.alert.table.field'),
      align: 'center',
      disableSort: true,
    },
    {
      id: 'message',
      label: t('xlsxValidation.alert.table.message'),
      align: 'center',
      disableSort: true,
    },
  ] as TableHeadItem<XlsxValidationError>[]

// ----------------------------------------------------------------------

type XlsxTablePropsType = {
  list: XlsxValidationByRow[]
  sx?: SxProps<Theme>
}

const XlsxValidationTable: React.FC<XlsxTablePropsType> = ({ list, sx }) => {
  const tableRef = useRef<HTMLTableElement>(null)

  const { t } = useTranslation()

  const tableHead = getTableHead(t)

  const haveItems = list?.length > 0

  const onRequestSort = () => {
    return null
  }

  return (
    <Card sx={sx} ref={tableRef}>
      <Scrollbar sx={{ height: '100%' }}>
        <TableContainer sx={{ height: '100%' }}>
          <Table>
            <TableHead
              size="xs"
              order={SortDirection.desc}
              orderBy={'field'}
              headLabel={tableHead}
              rowCount={list?.length}
              onRequestSort={onRequestSort}
            />

            {haveItems && (
              <TableBody>
                {list?.map(({ row, errors }, index) => (
                  <Fragment key={`fragment-${row}`}>
                    {index > 0 && (
                      <TableRow key={`row-${row}-empty`}>
                        <TableCell colSpan={2} align="center" />
                      </TableRow>
                    )}
                    <TableRow
                      key={`row-${row}`}
                      sx={(theme) => ({
                        backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                      })}
                    >
                      <TableCell colSpan={2} align="center" bold size="xs" color="primary.main">
                        {row === '_general'
                          ? t('xlsxValidation.alert.table.general')
                          : `${t('xlsxValidation.alert.table.row')} ${row}`}
                      </TableCell>
                    </TableRow>
                    {errors.map((error, index) => (
                      <XlsxValidationErrorRow
                        key={`${error.field}-${error.message}`}
                        item={error}
                        isEvenRow={index % 2 === 1}
                      />
                    ))}
                  </Fragment>
                ))}
              </TableBody>
            )}

            {!haveItems && (
              <TableBody>
                <TableRow>
                  <TableCell noBorder align="center" colSpan={tableHead.length} sx={{ py: 3 }}>
                    <EmptyData />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>
    </Card>
  )
}

const XlsxValidationErrorRow = ({
  item,
  isEvenRow,
}: {
  item: XlsxValidationError
  isEvenRow: boolean
}) => {
  const { field, message } = item

  const fieldLabel = startCase(field)

  return (
    <TableRow
      sx={(theme) => ({
        ...(isEvenRow && {
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
        }),
      })}
    >
      {field === '_general' ? (
        <TableCell colSpan={2} align="center" size="xs" width={'100%'}>
          {message}
        </TableCell>
      ) : (
        <>
          <TableCell align="center" size="xs" width={'50%'}>
            {fieldLabel}
          </TableCell>
          <TableCell align="center" size="xs" width={'50%'}>
            {message}
          </TableCell>
        </>
      )}
    </TableRow>
  )
}

export default XlsxValidationTable
